import React, { useState, useEffect } from 'react';
import { Zap, BarChart3, ChevronDown, Building, Globe, Wifi, Brain, FileText, Check, Activity, Radio, Landmark, ShieldAlert, Bot } from 'lucide-react';
import { usePermissions } from 'hooks/usePermissions';
import PageLayout from 'components/common/PageLayout';
import { getCompanyContext } from 'utils/setCompanyContext';

// Importar componentes das abas
import DashboardAutomacoes from '../components/DashboardAutomacoes';

// Importar dashboards específicos
import MovFibraDashboard from '../components/dashboards/MovFibraDashboard';
import MovFibraBotCobrancaDashboard from '../components/dashboards/MovFibraBotCobrancaDashboard';
import ReelunetDashboard from '../components/dashboards/ReelunetDashboard';
import ReelunetAlertaLosDashboard from '../components/dashboards/ReelunetAlertaLosDashboard';
import ReelunetGeracaoFinanceiroDashboard from '../components/dashboards/ReelunetGeracaoFinanceiroDashboard';
import ReelunetCobrancaBloqueadosDashboard from '../components/dashboards/ReelunetCobrancaBloqueadosDashboard';
import XtFibraDashboard from '../components/dashboards/XtFibraDashboard';
import MovFitDashboard from '../components/dashboards/MovFitDashboard';

const Automacoes = ({ initialTab = 'dashboard' }) => {
  const { hasPermission } = usePermissions();
  const [abaAtiva, setAbaAtiva] = useState(initialTab);
  const [dashboardSelecionado, setDashboardSelecionado] = useState(null);
  const [actionSelecionada, setActionSelecionada] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Obter empresa atual a partir do contexto global
  const [empresaFiltro, setEmpresaFiltro] = useState('all'); // all | mov-fibra | xt-fibra | movfit

  useEffect(() => {
    const atualizarEmpresaFiltro = () => {
      const context = getCompanyContext();
      const isGroup = context?.isGroup || context?.isConsolidated || (context?.companies?.length || 0) > 1;
      if (isGroup) {
        setEmpresaFiltro('all');
        return;
      }

      const slug = context?.companies?.[0]?.slug || '';
      const slugLower = slug.toLowerCase();

      if (slugLower.includes('xt')) {
        setEmpresaFiltro('xt-fibra');
      } else if (slugLower.includes('movfit') || slugLower.includes('mov-fit')) {
        setEmpresaFiltro('movfit');
      } else if (slugLower.includes('mov') || slugLower.includes('fibra')) {
        // Trata MOV Fibra e também onde Reelunet deve aparecer junto
        setEmpresaFiltro('mov-fibra');
      } else {
        setEmpresaFiltro('all');
      }
    };

    atualizarEmpresaFiltro();

    // Atualizar quando o contexto de empresa mudar
    const handler = () => atualizarEmpresaFiltro();
    window.addEventListener('company-context-changed', handler);
    window.addEventListener('company-changed', handler);

    return () => {
      window.removeEventListener('company-context-changed', handler);
      window.removeEventListener('company-changed', handler);
    };
  }, []);

  // Mapeamento de actions para cada dashboard
  const actionsMap = {
    'mov-fibra-ia-vendas': 'ai-vendas-selected',
    'reelunet-ia-vendas': 'reelunet-selected',
    'mov-fibra-servico-rede': 'servico-rede-selected',
    'mov-fibra-bot-cobranca': 'auto-selected',
    'reelunet': 'reelunet-selected',
    'reelunet-alerta-los': 'reelunet-selected',
    'reelunet-geracao-financeiro': 'reelunet-selected',
    'reelunet-cobranca-bloqueados': 'reelunet-selected',
    'xt-fibra': 'xt-fibra-selected',
    'movfit-ia-atendimento': 'movfit-ia-atendimento-selected'
  };

  // Opções do dropdown organizadas hierarquicamente
  const opcoesDropdown = [
    {
      id: 'mov-fibra',
      nome: 'MOV Fibra',
      icon: Building,
      subOpcoes: [
        {
          id: 'mov-fibra-ia-vendas',
          nome: 'IA de Vendas',
          descricao: 'Automações de IA para vendas',
          icon: Brain
        },
        {
          id: 'mov-fibra-servico-rede',
          nome: 'IA de Serviço de Redes',
          descricao: 'Automações de IA para serviço de redes',
          icon: Wifi
        },
        {
          id: 'mov-fibra-bot-cobranca',
          nome: 'BOT COBRANÇA',
          descricao: 'Métricas do bot de cobrança',
          icon: Bot
        }
      ]
    },
    {
      id: 'reelunet',
      nome: 'Reelunet',
      icon: Globe,
      subOpcoes: [
        {
          id: 'reelunet-ia-vendas',
          nome: 'IA de Vendas',
          descricao: 'Automações de IA para vendas',
          icon: Brain
        },
        {
          id: 'reelunet',
          nome: 'Assinatura de Processos',
          descricao: 'Automação de assinatura de processos',
          icon: FileText
        },
        {
          id: 'reelunet-alerta-los',
          nome: 'Alerta de LOS ativo',
          descricao: 'Monitoramento e alertas de LOS ativo',
          icon: Radio
        },
        {
          id: 'reelunet-geracao-financeiro',
          nome: 'Geração de Financeiro',
          descricao: 'Automação de geração financeira',
          icon: Landmark
        },
        {
          id: 'reelunet-cobranca-bloqueados',
          nome: 'Cobrança de bloqueados',
          descricao: 'Automação de cobrança para clientes bloqueados',
          icon: ShieldAlert
        }
      ]
    },
    {
      id: 'xt-fibra',
      nome: 'XT Fibra',
      icon: Wifi,
      subOpcoes: [
        {
          id: 'xt-fibra',
          nome: 'Finalização de OS',
          descricao: 'Automação de finalização de OS financeira',
          icon: FileText
        }
      ]
    },
    {
      id: 'movfit',
      nome: 'MOVFit',
      icon: Activity,
      subOpcoes: [
        {
          id: 'movfit-ia-atendimento',
          nome: 'IA de Atendimento',
          descricao: 'Automações de IA para atendimento',
          icon: Brain
        }
      ]
    }
  ];

  // Filtrar opções de automações conforme empresa selecionada na diretoria
  const opcoesDropdownFiltradas = opcoesDropdown.filter((grupo) => {
    if (empresaFiltro === 'all') return true;
    if (empresaFiltro === 'mov-fibra') {
      // MOV Fibra + Reelunet juntas
      return grupo.id === 'mov-fibra' || grupo.id === 'reelunet';
    }
    if (empresaFiltro === 'xt-fibra') {
      return grupo.id === 'xt-fibra';
    }
    if (empresaFiltro === 'movfit') {
      return grupo.id === 'movfit';
    }
    return true;
  });

  // Garantir que um dashboard selecionado incompatível com a empresa atual seja limpo
  useEffect(() => {
    if (!dashboardSelecionado) return;

    const aindaExiste = opcoesDropdownFiltradas.some((grupo) =>
      grupo.subOpcoes.some((sub) => sub.id === dashboardSelecionado)
    );

    if (!aindaExiste) {
      setDashboardSelecionado(null);
      setActionSelecionada(null);
    }
  }, [empresaFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  const abas = [
    {
      id: 'dashboard',
      nome: 'Dashboard',
      icon: BarChart3,
      cor: 'blue',
      permission: 'automacoes.view'
    }
  ];

  // Função para selecionar um dashboard
  const selecionarDashboard = (dashboardId) => {
    setDashboardSelecionado(dashboardId);
    setActionSelecionada(actionsMap[dashboardId]);
    setIsDropdownOpen(false);
  };

  // Função para obter o nome do dashboard selecionado
  const getNomeDashboardSelecionado = () => {
    for (const grupo of opcoesDropdownFiltradas) {
      for (const subOpcao of grupo.subOpcoes) {
        if (subOpcao.id === dashboardSelecionado) {
          return `${grupo.nome} - ${subOpcao.nome}`;
        }
      }
    }
    return 'Lista de Automações';
  };

  const handleRefreshFromHeader = async () => {
    setRefreshKey((k) => k + 1);
  };

  const renderizarAba = () => {
    // Se há um dashboard específico selecionado, renderizar ele
    if (dashboardSelecionado && actionSelecionada) {
      switch (dashboardSelecionado) {
        case 'mov-fibra-ia-vendas':
        case 'mov-fibra-servico-rede':
          return <MovFibraDashboard action={actionSelecionada} refreshKey={refreshKey} />;
        case 'mov-fibra-bot-cobranca':
          return <MovFibraBotCobrancaDashboard action={actionSelecionada} refreshKey={refreshKey} />;
        case 'reelunet-ia-vendas':
          return (
            <MovFibraDashboard
              action={actionSelecionada}
              refreshKey={refreshKey}
              variant="reelunet"
            />
          );
        case 'reelunet':
          return <ReelunetDashboard action={actionSelecionada} refreshKey={refreshKey} />;
        case 'reelunet-alerta-los':
          return (
            <ReelunetAlertaLosDashboard action={actionSelecionada} refreshKey={refreshKey} />
          );
        case 'reelunet-geracao-financeiro':
          return (
            <ReelunetGeracaoFinanceiroDashboard action={actionSelecionada} refreshKey={refreshKey} />
          );
        case 'reelunet-cobranca-bloqueados':
          return (
            <ReelunetCobrancaBloqueadosDashboard action={actionSelecionada} refreshKey={refreshKey} />
          );
        case 'xt-fibra':
          return <XtFibraDashboard action={actionSelecionada} refreshKey={refreshKey} />;
        case 'movfit-ia-atendimento':
          return <MovFitDashboard action={actionSelecionada} refreshKey={refreshKey} />;
        default:
          return <DashboardAutomacoes refreshKey={refreshKey} />;
      }
    }

    // Caso contrário, renderizar dashboard principal
    switch (abaAtiva) {
      case 'dashboard':
        return <DashboardAutomacoes refreshKey={refreshKey} />;
      default:
        return <DashboardAutomacoes refreshKey={refreshKey} />;
    }
  };

  // Verificar permissões
  const abasPermitidas = abas.filter(aba => hasPermission(aba.permission));

  return (
    <PageLayout 
      showRefreshButton={true}
      customRefreshFunction={handleRefreshFromHeader}
    >
      <div className="max-w-[90rem] mx-auto px-1.5 sm:px-2 lg:px-3 py-6">
        <div className="space-y-6">
          {/* Navegação das abas */}
          <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)]">
            <div className="border-b border-[color:var(--border)]">
              <nav className="flex space-x-8 px-6">
                {abasPermitidas.map((aba) => {
                  const IconComponent = aba.icon;
                  return (
                    <button
                      key={aba.id}
                      onClick={() => {
                        setAbaAtiva(aba.id);
                        setDashboardSelecionado(null);
                        setActionSelecionada(null);
                      }}
                      className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        abaAtiva === aba.id && !dashboardSelecionado
                          ? 'border-[color:var(--accent)] text-[color:var(--accent)]'
                          : 'border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:border-[color:var(--border)]'
                      }`}
                    >
                      <IconComponent className="h-4 w-4 mr-2" />
                      {aba.nome}
                    </button>
                  );
                })}
                
                {/* Dropdown Lista de Automações */}
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      dashboardSelecionado
                        ? 'border-[color:var(--accent)] text-[color:var(--accent)]'
                        : 'border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:border-[color:var(--border)]'
                    }`}
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    {getNomeDashboardSelecionado()}
                    <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isDropdownOpen && (
                    <>
                      {/* Overlay para fechar o dropdown */}
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setIsDropdownOpen(false)}
                      />
                      
                      {/* Dropdown menu */}
                      <div className="absolute top-full left-0 right-0 sm:left-auto sm:right-0 sm:w-80 md:w-80 lg:w-80 xl:w-80 mt-1 bg-[color:var(--surface)] rounded-lg shadow-lg border border-[color:var(--border)] z-50 max-h-96 overflow-y-auto">
                        <div className="py-1">
                          {opcoesDropdownFiltradas.map((grupo) => (
                            <div key={grupo.id}>
                              {/* Cabeçalho do grupo */}
                              <div className="px-3 py-2 bg-[color:var(--surface-muted)] border-b border-[color:var(--border)]">
                                <div className="flex items-center space-x-2">
                                  <grupo.icon className="h-4 w-4 text-[color:var(--text-muted)] flex-shrink-0" />
                                  <div className="min-w-0 flex-1 text-left">
                                    <div className="text-sm font-semibold text-[color:var(--text-primary)] break-words">
                                      {grupo.nome}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Subopções do grupo */}
                              {grupo.subOpcoes.map((subOpcao) => {
                                const SubIcon = subOpcao.icon;
                                return (
                                  <button
                                    key={subOpcao.id}
                                    onClick={() => selecionarDashboard(subOpcao.id)}
                                    className="w-full flex items-start justify-between px-3 py-2 text-left hover:bg-[color:var(--surface-muted)] transition-colors"
                                  >
                                    <div className="flex items-start space-x-2 min-w-0 flex-1">
                                      <div className="p-1 bg-[color:var(--surface-muted)] rounded flex-shrink-0 mt-0.5">
                                        <SubIcon className="h-4 w-4 text-[color:var(--text-muted)]" />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-[color:var(--text-primary)] break-words leading-tight">
                                          {subOpcao.nome}
                                        </div>
                                        <div className="text-xs text-[color:var(--text-muted)] break-words mt-1 leading-relaxed">
                                          {subOpcao.descricao}
                                        </div>
                                      </div>
                                    </div>
                                    {dashboardSelecionado === subOpcao.id && (
                                      <Check className="h-4 w-4 text-[color:var(--accent)] flex-shrink-0" />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </nav>
            </div>

            {/* Conteúdo da aba ativa */}
            <div className="p-6">
              {renderizarAba()}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Automacoes;
